<template>
    <div v-if="recentAttempts.length > 0" class="attempts-card" ref="container">
        <h3>Recent Attempts</h3>
        <div class="attempts-list">
            <div
                v-for="attempt in recentAttempts"
                :key="attempt.attempt_id"
                class="attempt-item"
                :class="attempt.is_correct ? 'correct' : 'incorrect'"
            >
                <div class="attempt-header">
                    <span class="attempt-id">#{{ attempt.question_id }}</span>
                    <span class="attempt-score">{{ attempt.score }}/{{ attempt.max_marks }}</span>
                </div>
                <div class="attempt-question">{{ attempt.question_text }}</div>
                <div class="attempt-meta">
                    <span class="badge">{{ attempt.difficulty }}</span>
                    <span class="badge">{{ attempt.question_type }}</span>
                    <span class="attempt-time">{{ formatDate(attempt.completed_at) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch, nextTick } from 'vue';
import renderMathInElement from 'katex/contrib/auto-render';
import "katex/dist/katex.min.css";

export default {
    name: 'RecentAttempts',
    props: {
        recentAttempts: {
            type: Array,
            required: true
        }
    },
    setup(props) {
        const container = ref(null);

        const formatDate = (dateString) => {
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + 
                   date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        };

        const renderLaTeX = async () => {
            await nextTick();
            if (container.value) {
                renderMathInElement(container.value, {
                    delimiters: [
                        { left: "$$", right: "$$", display: true },
                        { left: "\\[", right: "\\]", display: true },
                        { left: "\\(", right: "\\)", display: false },
                        { left: "$", right: "$", display: false },
                    ],
                    throwOnError: false,
                    errorCallback: (msg, err) => {
                        console.error("KaTeX error:", msg, err);
                    },
                });
            }
        };

        // Watch the prop. If the data changes, re-run the LaTeX scan.
        watch(() => props.recentAttempts, () => {
            renderLaTeX();
        }, { immediate: true });

        return {
            container,
            formatDate
        };
    }
};
</script>

<style scoped>
.attempts-card {
    background: var(--bg-secondary);
    padding: 30px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    transition: border-color 0.3s;
}

.attempts-card:hover {
    border-color: var(--accent);
}

.attempts-card h3 {
    margin: 0 0 20px 0;
    color: var(--text-secondary);
    font-size: 24px;
    letter-spacing: 2px;
}

.attempts-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.attempt-item {
    padding: 20px;
    border: 2px solid;
    transition: all 0.3s;
}

.attempt-item.correct {
    background: rgba(76, 175, 80, 0.1);
    border-color: #4caf50;
}

.attempt-item.correct:hover {
    background: rgba(76, 175, 80, 0.15);
    border-color: #66bb6a;
    transform: translateX(5px);
}

.attempt-item.incorrect {
    background: rgba(244, 67, 54, 0.1);
    border-color: #f44336;
}

.attempt-item.incorrect:hover {
    background: rgba(244, 67, 54, 0.15);
    border-color: #ef5350;
    transform: translateX(5px);
}

.attempt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.attempt-id {
    font-size: 24px;
    color: var(--text-primary);
}

.attempt-score {
    color: var(--text-secondary);
    font-weight: 700;
    font-size: 18px;
}

.attempt-question {
    color: var(--text-primary);
    margin-bottom: 10px;
    font-size: 15px;
    line-height: 1.5;
}

.attempt-meta {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
}

.badge {
    padding: 6px 12px;
    background: var(--bg-secondary);
    font-size: 12px;
    color: var(--text-secondary);
    text-transform: capitalize;
    border: 1px solid var(--accent);
}

.attempt-time {
    color: var(--text-primary);
    font-size: 13px;
    margin-left: auto;
    opacity: 0.7;
}
</style>